import { SILENT_ERROR_META } from '@hub/lib/queryMeta';
import { getSession, getSessionQueryKey } from '@platform/api';
import { queryOptions } from '@tanstack/react-query';

const FIVE_MINUTES = 5 * 60 * 1_000;

/**
 * Spelled out rather than spreading `getSessionOptions()`, because the probe needs its own `queryFn`:
 * a 401 here means "anonymous", not "session expired", and the wrap opts out of the fetch-layer
 * redirect (the refresh still runs) so public routes stay reachable. The probe then surfaces
 * `ApiError(401)`, which `AuthProvider` reads as unauthed.
 *
 * No local `retry` override: the shared `retryPolicy` already declines to retry `ApiError` (so a 401
 * settles immediately) while still riding out a transient network blip once — a local override would
 * only drift from that.
 */
export const sessionQueryOptions = queryOptions({
  queryKey: getSessionQueryKey(),
  queryFn: async ({ signal }) => {
    // Request-level meta (read by the transport interceptor): suppress the 401 redirect for this probe
    // so a 401 surfaces as `ApiError(401)` = anonymous. Distinct from the query `meta` below, which the
    // global error handler reads.
    const { data } = await getSession({ signal, throwOnError: true, meta: { skipUnauthorized: true } });
    return data;
  },
  staleTime: FIVE_MINUTES,
  meta: { ...SILENT_ERROR_META, sharedAcrossIdentities: true },
});
